import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Animated from 'react-native-reanimated';
import { debounce } from 'lodash';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { AppView, CardView, SettingsItemSwitch } from 'design-system';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteAll, useEventBusEffect, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { editSavingsPlan, updateSavingRules } from 'features/pots/actions';
import AutosaveRulesWarningModal from 'features/pots/components/AutosaveRulesWarningModal';
import { busEvents } from 'features/pots/constants';
import { selectAutosaveRulesArr } from 'features/pots/reducers/selectors';
import { SavingsPlanRuleType } from 'features/pots/types';
const AutosaveAISavingsSettings = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const [, , , fetch] = useFetchOld(muteAll);
    const store = useAppStore();
    const mountRef = useRef(false);
    const autosaveRulesArr = useAppSelector(selectAutosaveRulesArr);
    const savingsPlanId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const isPaused = useAppSelector((state) => state.pots.savingsPlan?.isPaused);
    const isAutomated = useAppSelector((state) => state.pots.savingsPlan?.automated);
    const [active, setActive] = useState(!!isAutomated && !isPaused);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_RULES}/>,
        });
    }, []);
    useEffect(() => {
        if (mountRef.current) {
            setActive(!!isAutomated);
        }
        else {
            mountRef.current = true;
        }
    }, [isAutomated]);
    useEventBusEffect(() => {
        setActive(!!store.getState().pots.savingsPlan?.automated && !store.getState().pots.savingsPlan?.isPaused);
    }, [busEvents.UPDATE_SAVING_RULES_FAILURE_EVENT]);
    const pauseAutosave = useCallback(() => {
        if (!savingsPlanId || isPaused)
            return;
        fetch(editSavingsPlan(savingsPlanId, { isPaused: true }));
    }, [isPaused, savingsPlanId]);
    const onSwitchEvent = useMemo(() => debounce(async (value) => {
        if (savingsPlanId) {
            const newRules = (() => {
                if (isPaused) {
                    return [{ type: SavingsPlanRuleType.AUTOMATED }];
                }
                return value
                    ? [...autosaveRulesArr, { type: SavingsPlanRuleType.AUTOMATED }]
                    : autosaveRulesArr.filter((rule) => rule.type !== SavingsPlanRuleType.AUTOMATED);
            })();
            setActive(value);
            if (!newRules.length) {
                AutosaveRulesWarningModal.show(pauseAutosave, () => {
                    setActive(!value);
                });
            }
            else {
                dispatch(updateSavingRules({
                    savingsPlanId,
                    rules: newRules,
                }));
            }
        }
    }, 200), [autosaveRulesArr, isPaused, pauseAutosave, savingsPlanId]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="AI savings" subTitle="Emma will calculate the perfect amount to set aside based on your spending and current account balance. This is great if you don’t know how to get started!"/>
          <CardView noMarginTop>
            <SettingsItemSwitch title="AI savings" value={active} onValueChange={onSwitchEvent}/>
          </CardView>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default AutosaveAISavingsSettings;
